import { useState, useEffect } from 'react';
import axios from 'axios';
import Api from '@api';

/**
 * Utility function to fetch authenticated image and return blob URL
 * This ensures vtt_user_signature header is automatically added via axios interceptor
 * 
 * @param {string} imagePath - The image path/URL (relative to imgUrl base)
 * @returns {Promise<string|null>} - Promise that resolves to blob URL, or null on error
 */
export const getAuthenticatedImageUrl = async (imagePath) => {
  if (!imagePath) {
    return null;
  }

  try {
    // Use GET request to the img-file endpoint
    // Axios interceptor will automatically add vtt_user_signature header
    const fullUrl = `${Api.imgUrl}${imagePath}`;
    const response = await axios.get(fullUrl, {
      responseType: 'blob',
    });

    if (response.data) {
      // Create blob URL from the response
      return URL.createObjectURL(response.data);
    }
    return null;
  } catch (error) {
    console.error('Error loading authenticated image:', error);
    return null;
  }
};

/**
 * Custom hook to fetch authenticated images via axios
 * This ensures vtt_user_signature header is automatically added via axios interceptor
 * 
 * @param {string} imagePath - The image path/URL (relative to imgUrl base)
 * @returns {string|null} - Blob URL for the image, or null if not loaded/error
 */
export const useAuthenticatedImage = (imagePath) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!imagePath) {
      setImageUrl(null);
      return;
    }

    let blobUrl = null;
    let isMounted = true;

    // Fetch image via axios (which will automatically add vtt_user_signature header)
    const fetchImage = async () => {
      try {
        // Use GET request to the img-file endpoint
        const fullUrl = `${Api.imgUrl}${imagePath}`;
        const response = await axios.get(fullUrl, {
          responseType: 'blob',
        });

        if (isMounted && response.data) {
          // Create blob URL from the response
          blobUrl = URL.createObjectURL(response.data);
          setImageUrl(blobUrl);
        }
      } catch (error) {
        console.error('Error loading authenticated image:', error);
        if (isMounted) {
          setImageUrl(null);
        }
      }
    };

    fetchImage();

    // Cleanup: revoke blob URL when component unmounts or imagePath changes
    return () => {
      isMounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [imagePath]);

  return imageUrl;
};

export default useAuthenticatedImage;
